/**
 * Minimal, standalone cross-runtime storage PROBE for the per-user temp token.
 *
 * Purpose: isolate whether OfficeRuntime.storage (and localStorage) actually share
 * data between the taskpane (full webview runtime) and the custom-functions
 * (JavaScript-only) runtime — independent of the fuller `tokenStorage` module and
 * its lazy-init / backend-latch logic.
 *
 * Key detail under test: this uses the BARE global `OfficeRuntime` (as Microsoft's
 * docs do), NOT `window.OfficeRuntime`. In the JS-only custom-functions runtime,
 * `window` can be a synthetic object whose `OfficeRuntime.storage` is a different,
 * per-runtime instance than the real shared global — which would explain why the
 * custom function sees an empty store while the taskpane's is full.
 *
 * Flow: the taskpane calls writeProbeToken() on login; the IAD "AUTHCHECK" formula
 * calls readProbeToken() and surfaces the result into the cell. TEMP — remove once
 * the storage question is settled.
 */

/* global OfficeRuntime, window, localStorage */
declare const OfficeRuntime: any;

const OFFICE_KEY = 'insync_probe_temp_token';
const LS_KEY = 'insync_probe_temp_token_ls';

/** The shared OfficeRuntime storage, preferring the bare global over window.* */
function officeStore(): any {
  const fromGlobal = typeof OfficeRuntime !== 'undefined' ? OfficeRuntime : undefined;
  const fromWindow = typeof window !== 'undefined' ? (window as any).OfficeRuntime : undefined;
  const rt = fromGlobal || fromWindow;
  return rt && rt.storage ? rt.storage : null;
}

/** True when the bare global and window.* resolve to the SAME OfficeRuntime object. */
export function officeRuntimeRefsMatch(): string {
  const g = typeof OfficeRuntime !== 'undefined' ? OfficeRuntime : undefined;
  const w = typeof window !== 'undefined' ? (window as any).OfficeRuntime : undefined;
  if (!g && !w) return 'neither';
  if (!g) return 'window-only';
  if (!w) return 'global-only';
  return g === w ? 'same' : 'different';
}

/** Write the probe token to the bare-global OfficeRuntime.storage AND localStorage. */
export async function writeProbeToken(token: string): Promise<void> {
  const value = `${token}|${Date.now()}`;
  try {
    await officeStore()?.setItem(OFFICE_KEY, value);
  } catch {
    /* ignore — read side reports absence */
  }
  try {
    localStorage.setItem(LS_KEY, value);
  } catch {
    /* ignore */
  }
}

/** Read the probe token back from both stores (whatever THIS runtime can see). */
export async function readProbeToken(): Promise<{ office: string | null; ls: string | null; refs: string }> {
  let office: string | null = null;
  let ls: string | null = null;
  try {
    office = (await officeStore()?.getItem(OFFICE_KEY)) ?? null;
  } catch {
    /* ignore */
  }
  try {
    ls = localStorage.getItem(LS_KEY);
  } catch {
    /* ignore */
  }
  return { office, ls, refs: officeRuntimeRefsMatch() };
}
